import { ProgressSpinner } from "primereact/progressspinner";
import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import FormValidation from "../../../Common/FormValidation";
import Http from "../../../Common/Http";
import IsLogin from "../../../Common/IsLogin";
import { ChartOfAccountStyles } from "../Chart-Of-Account/ChartOfAccountShared";
import CostCenterForm from "./CostCenterForm";

const Edit = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [inputs, setInputs] = useState({
    code: "",
    name: "",
    type: "department",
    parent_id: "",
    department_id: "",
    acc_customer_id: "",
    effective_date: "",
    budget_amount: "",
    description: "",
    status: "active",
  });
  const [options, setOptions] = useState({
    parents: [],
    types: [],
    departments: [],
    customers: [],
  });
  const [selectedParent, setSelectedParent] = useState(null);
  const [selectedDepartment, setSelectedDepartment] = useState(null);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [error, setError] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);

      try {
        // `exclude` flags this centre and its own children in the parent list,
        // so the picker cannot be used to build a cycle.
        const [costCenterRes, optionRes] = await Promise.all([
          Http.get("/cost-center/" + id + "/edit"),
          Http.get("/cost-center-form-options?exclude=" + id),
        ]);

        const data = costCenterRes.data;

        setInputs({
          code: data.code ?? "",
          name: data.name ?? "",
          type: data.type ?? "department",
          parent_id: data.parent_id ?? "",
          department_id: data.department_id ?? "",
          acc_customer_id: data.acc_customer_id ?? "",
          effective_date: data.effective_date ?? "",
          budget_amount: data.budget_amount ?? "",
          description: data.description ?? "",
          status: data.status ?? "active",
        });

        setOptions(optionRes.data);

        const find = (list, value) =>
          (list || []).find((option) => option.value === value) || null;

        setSelectedParent(find(optionRes.data.parents, data.parent_id));
        setSelectedDepartment(
          find(optionRes.data.departments, data.department_id),
        );
        setSelectedCustomer(
          find(optionRes.data.customers, data.acc_customer_id),
        );
      } catch (e) {
        console.error("Fetch error:", e);
        setError({
          load: [
            "Could not load the cost center. Please refresh and try again.",
          ],
        });
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setInputs((values) => ({ ...values, [name]: value }));
  };

  const selectHandleChange = (setSelected, field) => (selectedOption) => {
    setSelected(selectedOption);
    setInputs((values) => ({
      ...values,
      [field]: selectedOption ? selectedOption.value : "",
    }));
  };

  const validate = () => {
    const errors = {};

    if (!String(inputs.code).trim()) {
      errors.code = ["Code is required."];
    }
    if (!String(inputs.name).trim()) {
      errors.name = ["Name is required."];
    }
    if (!inputs.type) {
      errors.type = ["Type is required."];
    }

    setError(errors);
    return Object.keys(errors).length === 0;
  };

  const formSubmit = () => {
    if (saving || !validate()) {
      return;
    }

    setSaving(true);

    Http.put("/cost-center/" + id, {
      ...inputs,
      parent_id: inputs.parent_id || null,
      department_id: inputs.department_id || null,
      acc_customer_id: inputs.acc_customer_id || null,
      effective_date: inputs.effective_date || null,
      budget_amount: inputs.budget_amount === "" ? 0 : inputs.budget_amount,
    })
      .then((res) => {
        navigate("/cost-center", {
          state: {
            message: res.data.message,
            type: res.data.type || "success",
          },
        });
      })
      .catch((e) => {
        setError(
          e.response?.data?.errors || {
            general: [
              e.response?.data?.message ||
                "Could not save the cost center. Please try again.",
            ],
          },
        );
      })
      .finally(() => setSaving(false));
  };

  return (
    <div>
      <IsLogin></IsLogin>
      <div className="am-mainpanel">
        <ChartOfAccountStyles />
        <div className="am-pagetitle">
          <div>
            <h5 className="am-title mb-1">
              Edit Cost Center{" "}
              {inputs.code && <span className="coa-code">{inputs.code}</span>}
            </h5>
            <p className="text-muted mb-0 tx-12">
              {inputs.name || `Cost center #${id}`}
            </p>
          </div>
          <Link to="/cost-center" className="btn btn-outline-secondary">
            <i className="fa fa-arrow-left mg-r-6"></i>Back to List
          </Link>
        </div>

        <div className="am-pagebody">
          <div className="row">
            <div className="col-lg">
              <FormValidation error={error} />
            </div>
          </div>

          {loading ? (
            <div className="coa-panel">
              <div className="coa-empty">
                <ProgressSpinner
                  style={{ width: "42px", height: "42px" }}
                  strokeWidth="6"
                />
              </div>
            </div>
          ) : (
            <CostCenterForm
              inputs={inputs}
              options={options}
              selectedParent={selectedParent}
              selectedDepartment={selectedDepartment}
              selectedCustomer={selectedCustomer}
              onChange={handleChange}
              onParentChange={selectHandleChange(
                setSelectedParent,
                "parent_id",
              )}
              onDepartmentChange={selectHandleChange(
                setSelectedDepartment,
                "department_id",
              )}
              onCustomerChange={selectHandleChange(
                setSelectedCustomer,
                "acc_customer_id",
              )}
              onSubmit={formSubmit}
              saving={saving}
              submitLabel="Update Cost Center"
              parentHint="This cost center and its own children cannot be chosen."
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default Edit;
